import env from '@/configs/env';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  token?: string;
  body?: unknown;
  baseUrl?: string;
  params?: Record<string, string>;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string
  ) {
    super(message);
    if (env.VITE_NODE_ENV === 'development') {
      console.error(
        `API Error [${status}]: ${message} ${code ? `| code: ${code}` : ''}`
      );
    }
  }

  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  static isClientError(error: unknown): boolean {
    return (
      ApiError.isApiError(error) && error.status >= 400 && error.status < 500
    );
  }

  static isServerError(error: unknown): boolean {
    return ApiError.isApiError(error) && error.status >= 500;
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || env.VITE_SERVER_URL;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { token, body, baseUrl, params, ...fetchOptions } = options;

    const url = `${baseUrl || this.baseUrl}${endpoint}${
      params ? `?${new URLSearchParams(params).toString()}` : ''
    }`;

    const config: RequestInit = {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...fetchOptions.headers,
      },
    };

    if (body !== undefined) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          // If parsing fails, use the raw text or fallback message
          errorMessage = errorText || errorMessage;
        }

        throw new ApiError(response.status, errorMessage);
      }

      // Handle empty responses (like 204 No Content)
      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return undefined as T;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      // For non-JSON responses, return the response object itself
      return response as unknown as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network errors, timeouts, etc.
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new ApiError(
          0,
          'Network error: Failed to fetch. Please check your internet connection.'
        );
      }

      throw new ApiError(
        0,
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }

  // Convenience methods
  async get<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export a singleton instance
const apiClient = new ApiClient();

export default apiClient;

export { ApiError };
